import moment from 'moment'
import { useApolloClient } from '@apollo/client'
import { useState, useEffect, useContext } from 'react'
import { affiliateEarningFindMany } from '../../../graphql/AffiliateEarning/affiliateEarningFindMany'
import { userStore } from '../../utils/UserContext'
import {
  AffiliateEarningFindMany,
  AffiliateEarningFindMany_affiliateEarningFindMany
} from '../../../graphql/AffiliateEarning/__generated__/AffiliateEarningFindMany'
import { affiliateLinkClickCount } from '../../../graphql/AffiliateLinkClick/affiliateLinkClickCount'
import { AffiliateEarningFindBetweenDates } from '../../../graphql/AffiliateEarning/__generated__/AffiliateEarningFindBetweenDates'
import { affiliateEarningFindBetweenDates } from '../../../graphql/AffiliateEarning/affiliateEarningFindBetweenDates'

// Loads all the data associated with the affiliate panel
const useAffiliateDashboard = () => {
  const apollo = useApolloClient()
  const { user } = useContext(userStore)

  const [dateRange, setDateRange] = useState(localStorage.getItem('affiliate-date-range') || '30')
  const [loading, setLoading] = useState(true)
  const [foreverEarnings, setForeverEarnings] = useState(0)
  const [totalEarnings, setTotalEarnings] = useState(0)
  const [totalClicks, setTotalClicks] = useState(0)
  const [totalSales, setTotalSales] = useState(0)
  const [trialingUsers, setTrialingUsers] = useState(0)
  const [earnings, setEarnings] = useState<AffiliateEarningFindMany_affiliateEarningFindMany[]>([])

  // Retrieves the appropriate timeframe
  const getDateAgo = () => {
    if (dateRange === 'year') {
      return moment()
        .utc()
        .startOf('year')
        .toDate()
    }

    return moment()
      .utc()
      .subtract(parseInt(dateRange || '0'), 'days')
      .startOf('day')
      .toDate()
  }

  // Loads the affiliate's earnings since they've started
  const loadForever = async () => {
    const { data } = await apollo.query<AffiliateEarningFindMany>({
      fetchPolicy: 'no-cache',
      query: affiliateEarningFindMany,
      variables: { affiliate: user?.id }
    })

    const affiliateEarnings = data.affiliateEarningFindMany
    const totalEarnings = affiliateEarnings.reduce((total, earning) => {
      return total + earning.amount
    }, 0)

    setForeverEarnings(totalEarnings)
  }

  // Loads every click on the affiliate's link during the time frame
  const loadLinkClicks = async (variables: Record<string, any>) => {
    const { data } = await apollo.query({
      fetchPolicy: 'no-cache',
      query: affiliateLinkClickCount,
      variables
    })

    setTotalClicks(data.affiliateLinkClickCount)
  }

  // Loads affiliate dashboard's overview during the time frame
  const loadOverviewData = async () => {
    const timeAgo = getDateAgo()
    const now = moment()
      .utc()
      .toDate()

    const variables = {
      affiliate: user?.id,
      greaterThanDate: timeAgo,
      lessThanDate: now
    }

    const { data } = await apollo.query<AffiliateEarningFindBetweenDates>({
      fetchPolicy: 'no-cache',
      query: affiliateEarningFindBetweenDates,
      variables
    })

    await loadLinkClicks(variables)

    const affiliateEarnings = data.affiliateEarningFindMany
    const trialingUsersAmount = affiliateEarnings.filter(
      (earning) => earning.trialStartDate && !earning.userPaidDate
    ).length
    const totalSalesAmount = affiliateEarnings.filter((earning) => earning.userPaidDate).length

    const totalEarnings = affiliateEarnings.reduce((total, earning) => {
      return total + earning.amount
    }, 0)

    setTotalEarnings(totalEarnings)
    setTrialingUsers(trialingUsersAmount)
    setTotalSales(totalSalesAmount)

    setEarnings(affiliateEarnings)
  }

  // Harness function - Handles loading all the data & the loading state
  const loadDashboardData = async () => {
    setLoading(true)
    await loadForever()
    await loadOverviewData()
    setLoading(false)
  }

  useEffect(() => {
    if (user?.payPalToken) {
      loadDashboardData()
      localStorage.setItem('affiliate-date-range', dateRange)
    }
  }, [user, dateRange])

  return {
    loading,
    foreverEarnings,
    earnings,
    dateRange,
    totalEarnings,
    totalSales,
    totalClicks,
    trialingUsers,
    actions: { setDateRange }
  }
}

export default useAffiliateDashboard
